import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Paper,
    Switch,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { setLoading } from 'Actions/loading';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import http from 'Utils/http';
import { process } from 'Utils/errorHandler';
import { useCopyText } from 'Utils/clipboard';
import { SigningKeys } from 'Shared/SigningKeys';

const DEFAULT_TOKEN_TTL = 3600;
const EMPTY_FORM = { name: '', flowIds: [], tokenTtl: DEFAULT_TOKEN_TTL };

const ApiClientsPage = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const copy = useCopyText();
    const isAdmin = useSelector((state) => state.auth.user.access === 'Admin');
    const companies = useSelector((state) => state.companies.companies || []);
    const ownCompanyId = useSelector((state) => state.auth.user.company_id);

    const [companyId, setCompanyId] = useState(ownCompanyId ?? '');
    const [clients, setClients] = useState([]);
    const [flows, setFlows] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [issued, setIssued] = useState(null);
    const [tab, setTab] = useState('clients');

    const companyQuery = isAdmin && companyId ? `?company_id=${companyId}` : '';

    const load = useCallback(() => {
        dispatch(setLoading(true));
        Promise.all([
            http.get(`/oauth/clients${companyQuery}`),
            http.get(`/oauth/clients/flows${companyQuery}`),
        ])
            .then(([clientsRes, flowsRes]) => {
                setClients(clientsRes.data.clients || []);
                setFlows(flowsRes.data.flows || []);
            })
            .catch((err) => process(err, enqueueSnackbar))
            .finally(() => dispatch(setLoading(false)));
    }, [companyQuery, dispatch, enqueueSnackbar]);

    useEffect(() => {
        load();
    }, [load]);

    const request = (promise, onSuccess) => {
        dispatch(setLoading(true));
        promise
            .then((res) => {
                onSuccess(res.data);
                load();
            })
            .catch((err) => process(err, enqueueSnackbar))
            .finally(() => dispatch(setLoading(false)));
    };

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setEditing('new');
    };

    const openEdit = (client) => {
        setForm({ name: client.name, flowIds: client.flow_ids, tokenTtl: client.token_ttl });
        setEditing(client);
    };

    const save = () => {
        const body = { name: form.name, flow_ids: form.flowIds, token_ttl: Number(form.tokenTtl) };
        if (editing === 'new') {
            request(
                http.post('/oauth/client/create', isAdmin ? { ...body, company_id: companyId } : body),
                (data) => setIssued({ clientId: data.client.client_id, clientSecret: data.client_secret }),
            );
        } else {
            request(http.post(`/oauth/client/${editing.id}/update`, body), () => {
                enqueueSnackbar('Client has been updated', { action: CustomSnackbar, variant: 'success' });
            });
        }
        setEditing(null);
    };

    const toggleActive = (client) => {
        request(http.post(`/oauth/client/${client.id}/update`, { is_active: !client.is_active }), () => {
            enqueueSnackbar(client.is_active ? 'Client has been disabled' : 'Client has been enabled', { action: CustomSnackbar, variant: 'success' });
        });
    };

    const rotateSecret = (client) => {
        request(http.post(`/oauth/client/${client.id}/rotate-secret`), (data) => {
            setIssued({ clientId: data.client.client_id, clientSecret: data.client_secret });
        });
    };

    const flowNames = (flowIds) => flows.filter((flow) => flowIds.includes(flow.id)).map((flow) => flow.name).join(', ');

    return (
        <>
            <Typography
                variant="h1"
                sx={{
                    textAlign: 'center',
                    marginBottom: '25px',
                    fontSize: '3rem',
                    fontWeight: 500,
                }}
            >
                API Clients
            </Typography>
            <Box component={Paper} sx={{ padding: '20px' }}>
                {isAdmin && (
                    <Tabs value={tab} onChange={(e, value) => setTab(value)} sx={{ marginBottom: '20px' }}>
                        <Tab value="clients" label="Clients" />
                        <Tab value="keys" label="Signing keys" />
                    </Tabs>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px' }}>
                    {isAdmin && (
                        <TextField
                            select
                            label="Company"
                            value={companyId}
                            onChange={(e) => setCompanyId(e.target.value)}
                            sx={{ minWidth: '240px' }}
                        >
                            {companies.map((company) => (
                                <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                            ))}
                        </TextField>
                    )}
                    {tab === 'clients' && (
                        <Button variant="contained" onClick={openCreate} disabled={!companyId} sx={{ marginLeft: 'auto' }}>
                            Create client
                        </Button>
                    )}
                    <Button
                        component="a"
                        href={`${http.defaults.baseURL}/docs/api-clients`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ marginLeft: tab === 'clients' ? 0 : 'auto' }}
                    >
                        Help
                    </Button>
                </Box>
                {tab === 'keys' && <SigningKeys companyId={companyId} />}
                {tab === 'clients' && <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Client ID</TableCell>
                                <TableCell>Allowed flows</TableCell>
                                <TableCell>Token lifetime, sec</TableCell>
                                <TableCell>Active</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {clients.map((client) => (
                                <TableRow key={client.id}>
                                    <TableCell>{client.name}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{client.client_id}</TableCell>
                                    <TableCell>{flowNames(client.flow_ids)}</TableCell>
                                    <TableCell>{client.token_ttl}</TableCell>
                                    <TableCell>
                                        <Switch checked={client.is_active} onChange={() => toggleActive(client)} inputProps={{ 'aria-label': `Active ${client.name}` }} />
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Button onClick={() => openEdit(client)}>Edit</Button>
                                        <Button onClick={() => rotateSecret(client)}>Rotate secret</Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!clients.length && (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ color: '#99a1ad' }}>No API clients yet</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>}
            </Box>

            <Dialog open={editing !== null} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
                <DialogTitle>{editing === 'new' ? 'Create client' : 'Edit client'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '20px', paddingTop: '10px !important' }}>
                    <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} inputProps={{ maxLength: 255 }} />
                    <Autocomplete
                        multiple
                        options={flows}
                        getOptionLabel={(flow) => flow.name}
                        value={flows.filter((flow) => form.flowIds.includes(flow.id))}
                        onChange={(e, value) => setForm({ ...form, flowIds: value.map((flow) => flow.id) })}
                        renderInput={(params) => <TextField {...params} label="Allowed flows" />}
                    />
                    <TextField
                        label="Token lifetime, sec"
                        type="number"
                        value={form.tokenTtl}
                        onChange={(e) => setForm({ ...form, tokenTtl: e.target.value })}
                        inputProps={{ min: 60, max: 86400 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setEditing(null)}>Close</Button>
                    <Button variant="contained" onClick={save} disabled={!form.name.trim()}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={issued !== null} onClose={() => setIssued(null)} fullWidth maxWidth="sm">
                <DialogTitle>Client credentials</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <Typography sx={{ color: '#3B465C' }}>
                        Copy the secret now. It is shown only once and cannot be recovered; rotate it to get a new one.
                    </Typography>
                    {issued && [['Client ID', issued.clientId], ['Client secret', issued.clientSecret]].map(([label, value]) => (
                        <Box key={label} sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <TextField label={label} value={value} fullWidth InputProps={{ readOnly: true }} />
                            <Button onClick={() => copy(value, label)}>Copy</Button>
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setIssued(null)}>Done</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ApiClientsPage;
